import { Controller } from '@hotwired/stimulus'

declare const Stripe: any

export default class extends Controller {
  static targets = [
    'billingDetailsContainer',
    'billingDetailsInput',
    'form',
    'billingDetailsCheckbox',
    'cardErrors',
    'addressErrors',
    'cardElement',
    'countryCode',
    'addressElement',
    'addressLine1',
    'addressLine2',
    'addressCity',
    'addressState',
    'addressPostalCode',
    'addressCountry'
  ]

  static values = {
    stripePublishableKey: String,
    address: Boolean,
    amount: Number
  }

  declare billingDetailsContainerTarget: HTMLDivElement
  declare billingDetailsInputTarget: HTMLTextAreaElement
  declare formTarget: HTMLFormElement
  declare billingDetailsCheckboxTarget: HTMLInputElement
  declare cardErrorsTarget: HTMLDivElement
  declare addressErrorsTarget: HTMLDivElement
  declare cardElementTarget: HTMLDivElement
  declare countryCodeTarget: HTMLInputElement
  declare hasCardErrorsTarget: boolean
  declare hasAddressErrorsTarget: boolean
  declare hasBillingDetailsCheckboxTarget: boolean
  declare hasBillingDetailsInputTarget: boolean
  declare hasBillingDetailsContainerTarget: boolean
  declare hasAddressElementTarget: boolean
  declare addressElementTarget: HTMLDivElement | undefined
  declare addressLine1Target: HTMLInputElement | undefined
  declare addressLine2Target: HTMLInputElement | undefined
  declare addressCityTarget: HTMLInputElement | undefined
  declare addressStateTarget: HTMLInputElement | undefined
  declare addressPostalCodeTarget: HTMLInputElement | undefined
  declare addressCountryTarget: HTMLInputElement | undefined
  declare addressValue: boolean
  declare stripePublishableKeyValue: string
  declare amountValue: number

  private stripe: any
  private cardElement: any
  private addressElement: any
  private elements: any
  private addressState: any = {}
  private addressComplete = false

  electionBuddyDetailsPerCountry = {
    CA: {
      name: "ElectionBuddy Inc.",
      address: "9622 42 Avenue #210 Edmonton AB CA",
      taxId: "865070783"
    },
    US: {
      name: "ElectionBuddy Inc.",
      address: "5301 S Superstition Mtn. Dr. Suite 104 #137 Gold Canyon AZ US",
      taxId: "98-1050820"
    }
  }

  connect() {
    console.log('[CreditCardForm] Controller connected')
    console.log('[CreditCardForm] addressValue:', this.addressValue)
    console.log('[CreditCardForm] hasAddressElementTarget:', this.hasAddressElementTarget)

    this.initializeCardElement()

    if (this.addressValue && this.hasAddressElementTarget) {
      this.initializeAddressElement()
    }
  }

  disconnect() {
  }

  public handleTaxIdChange(): void {
    // Only update if the checkbox is checked
    if (this.billingDetailsCheckboxTarget.checked && !this.billingDetailsContainerTarget.classList.contains('hide')) {
      this.populateBillingDetailsWithAddressAndTaxId()
    }
  }

  private initializeCardElement() {
    if (!this.stripePublishableKeyValue) {
      console.warn('[CreditCardForm] Stripe publishable key not available')
      return
    }

    console.log('[CreditCardForm] Initializing Card Element')

    if (!this.stripe) {
      this.stripe = Stripe(this.stripePublishableKeyValue)
    }

    this.elements = this.stripe.elements()
    this.cardElement = this.elements.create('card', { hidePostalCode: true })

    console.log('[CreditCardForm] Card element created, mounting to target')
    this.cardElement.mount(`#${this.cardElementTarget.id}`)

    console.log('[CreditCardForm] Card Element mounted successfully')
  }

  private initializeAddressElement() {
    if (!this.stripe) {
      console.warn('[CreditCardForm] Stripe publishable key not available')
      return
    }

    console.log('[CreditCardForm] Initializing Address Element')

    // Use amount from data or fallback to 1 cent for Address Element initialization
    const amountForAddressElement = this.amountValue && this.amountValue > 0 ? this.amountValue : 1

    const elements = this.stripe.elements({
      mode: 'payment',
      amount: amountForAddressElement,
      currency: 'usd'
    })

    const elementStyles = {
      base: {
        fontFamily: 'Lato, sans-serif',
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#9ca7af'
        }
      },
      invalid: {
        color: '#fa755a'
      }
    }

    this.addressElement = elements.create('address', {
      mode: 'billing',
      style: elementStyles
    })

    const addressContainer = document.querySelector('#stripe_address');
    console.log('[CreditCardForm] Address container element:', addressContainer);

    if (!addressContainer) {
      console.error('[CreditCardForm] Address container element not found')
      return
    }

    try {
      this.addressElement.mount('#stripe_address')
      console.log('[CreditCardForm] Address Element mounted successfully')
    } catch (error) {
      console.error('[CreditCardForm] Failed to mount Address Element:', error)
      return
    }

    // Listen to address element changes
    this.addressElement.on('change', (event: any) => {
      this.addressState = event.value?.address || {}
      this.addressComplete = Boolean(event.complete)
      this.clearAddressError()
      this.updateAddressFields()
    })
  }

  private updateAddressFields() {
    if (this.hasAddressLine1Target && this.addressLine1Target) {
      this.addressLine1Target.value = this.addressState.line1 || ''
    }
    if (this.hasAddressLine2Target && this.addressLine2Target) {
      this.addressLine2Target.value = this.addressState.line2 || ''
    }
    if (this.hasAddressCityTarget && this.addressCityTarget) {
      this.addressCityTarget.value = this.addressState.city || ''
    }
    if (this.hasAddressStateTarget && this.addressStateTarget) {
      this.addressStateTarget.value = this.addressState.state || ''
    }
    if (this.hasAddressPostalCodeTarget && this.addressPostalCodeTarget) {
      this.addressPostalCodeTarget.value = this.addressState.postal_code || ''
    }
    if (this.hasAddressCountryTarget && this.addressCountryTarget) {
      this.addressCountryTarget.value = this.addressState.country || ''
    }
  }

  submit(event: Event) {
    event.preventDefault()

    // Validation will be handled by Stripe Address Element
    // If address is required and invalid, Stripe will prevent submission
    this.appendCompanyDetailsOnSubmit()

    if (this.addressValue && this.hasAddressElementTarget && !this.validateAddress()) {
      return
    }

    if (!this.stripe || !this.cardElement) {
      console.warn('[CreditCardForm] Stripe not initialized, cannot submit')
      return
    }

    this.stripe.createToken(this.cardElement).then((result: any) => {
      if (result.error) {
        console.warn('[CreditCardForm] Stripe token error:', result.error.message)
        if (this.hasCardErrorsTarget) {
          this.cardErrorsTarget.textContent = result.error.message
        }
        return
      }

      if (result.token) {
        if (this.hasCardErrorsTarget) {
          this.cardErrorsTarget.textContent = ''
        }
        this.submitToken(result.token.id)
      }
    })
  }

  private validateAddress(): boolean {
    if (this.addressComplete) {
      this.clearAddressError()
      return true
    }

    if (this.hasAddressErrorsTarget) {
      this.addressErrorsTarget.textContent = 'Please complete your billing address.'
    }

    return false
  }

  private clearAddressError(): void {
    if (this.hasAddressErrorsTarget) {
      this.addressErrorsTarget.textContent = ''
    }
  }

  private submitToken(tokenId: string): void {
    const hiddenInput = document.createElement('input')
    hiddenInput.setAttribute('type', 'hidden')
    hiddenInput.setAttribute('name', 'stripe_token')
    hiddenInput.setAttribute('value', tokenId)
    this.formTarget.appendChild(hiddenInput)
    this.formTarget.submit()
  }

  public toggleBillingDetails(): void {
    this.billingDetailsContainerTarget.classList.toggle('hide')

    // When unchecking, clear the textarea
    if (this.billingDetailsContainerTarget.classList.contains('hide')) {
      this.billingDetailsInputTarget.value = ''
      return
    }

    // When checking, pre-fill with address + tax ID
    this.populateBillingDetailsWithAddressAndTaxId()
  }

  private appendCompanyDetailsOnSubmit(): void {
    if (!this.hasBillingDetailsCheckboxTarget || !this.hasBillingDetailsInputTarget) {
      return
    }

    if (!this.billingDetailsCheckboxTarget.checked) {
      this.billingDetailsInputTarget.value = ''
      return
    }

    const currentValue = this.billingDetailsInputTarget.value
    const ebDetails = this.obtainElectionBuddyDetails()

    // Append ElectionBuddy details (hidden) to the submitted value if not already there
    if (currentValue && !currentValue.includes("ElectionBuddy Inc.")) {
      this.billingDetailsInputTarget.value = `${currentValue}${ebDetails}`
    } else if (!currentValue) {
      this.billingDetailsInputTarget.value = ebDetails
    }
  }

  private populateBillingDetailsWithAddressAndTaxId(): void {
    const addressLines = this.buildAddressString()
    const taxIdLine = this.getTaxIdLine()

    let content = ''
    if (addressLines) content += addressLines
    if (taxIdLine) content += (content ? '\n' : '') + taxIdLine

    this.billingDetailsInputTarget.value = content
  }

  private buildAddressString(): string {
    const parts: string[] = []

    const line1 = this.addressLine1Target?.value
    if (line1) parts.push(line1)

    const line2 = this.addressLine2Target?.value
    if (line2) parts.push(line2)

    const city = this.addressCityTarget?.value
    const state = this.addressStateTarget?.value
    const postalCode = this.addressPostalCodeTarget?.value
    const country = this.addressCountryTarget?.value

    if (city || state || postalCode) {
      const cityStateZip = [city, state, postalCode].filter(Boolean).join(', ')
      if (cityStateZip) parts.push(cityStateZip)
    }
    if (country) parts.push(country)

    return parts.join('\n')
  }

  private getTaxIdLine(): string {
    const taxIdElement = document.getElementById('tax_id') as HTMLInputElement | null
    const taxId = taxIdElement?.value?.trim()
    return taxId ? `Tax ID: ${taxId}` : ''
  }

  private obtainElectionBuddyDetails(): string {
    const countryCode = this.countryCodeTarget.value
    const details = this.electionBuddyDetailsPerCountry[countryCode] || this.electionBuddyDetailsPerCountry.CA

    return `\n${details.name}\n${details.address}\nTax ID: ${details.taxId}`
  }
}
